/* eslint-disable @typescript-eslint/no-explicit-any */

// Função auxiliar recursiva para processar múltiplos níveis de dropdown
const processDropdownItems = (
  items: any[],
  allItems: any[],
  parentDropdownId?: string,
): any[] => {
  return items?.map((item: any) => {
    // Obter o buttonId do item (pode ser buttonId ou id)
    const itemButtonId = item?.buttonId || item?.id;

    // Buscar sub-itens que têm este item como parent
    const subItems = allItems?.filter(
      (subItem: any) => subItem?.relativeParentId === itemButtonId,
    );

    const processedItem: any = {
      buttonId: itemButtonId,
      buttonText: item?.buttonText,
      buttonOnClickFunc: item?.buttonOnClickFunc,
      buttonIsActive: item?.buttonIsActive || false,
      buttonIsDisabled: item?.buttonIsDisabled || false,
    };

    // Se houver sub-itens, processar recursivamente
    if (subItems && subItems.length > 0) {
      // Gerar dropdownId único para este nível
      const currentDropdownId = parentDropdownId
        ? `${parentDropdownId}-sub-${itemButtonId}`
        : `dropdown--${itemButtonId}`;

      processedItem.buttonDropdownMenuListData = processDropdownItems(
        subItems,
        allItems,
        currentDropdownId,
      );
    }

    return processedItem;
  });
};

// Função auxiliar para extrair itens do dropdown para o menu secundário
// Esta função mantém a hierarquia: itens do nível 3 ficam dentro do dropdown do nível 2
// IMPORTANTE: Esta função retorna APENAS os itens do nível 2, não os do nível 3
const extractDropdownItemsForSecondaryMenu = (
  dropdownItems: any[],
  allMenuItems: any[],
  includeNestedItems = true,
): any[] => {
  if (!dropdownItems || !Array.isArray(dropdownItems)) {
    return [];
  }

  return dropdownItems.map((item: any) => {
    const itemButtonId = item?.buttonId || item?.id;

    const secondaryMenuItem: any = {
      buttonId: itemButtonId,
      buttonText: item?.buttonText,
      buttonOnClickFunc: item?.buttonOnClickFunc,
      buttonIsActive: item?.buttonIsActive || false,
      buttonIsDisabled: item?.buttonIsDisabled || false,
    };

    // Se o item já tem buttonDropdownMenuListData (itens do nível 3 já processados),
    // usar diretamente em vez de buscar novamente
    if (
      item.buttonDropdownMenuListData &&
      item.buttonDropdownMenuListData.length > 0
    ) {
      // Usar os sub-itens que já estão no buttonDropdownMenuListData
      secondaryMenuItem.buttonDropdownMenuListData =
        item.buttonDropdownMenuListData.map((subItem: any) => ({
          buttonId: subItem?.buttonId || subItem?.id,
          buttonText: subItem?.buttonText,
          buttonOnClickFunc: subItem?.buttonOnClickFunc,
          buttonIsActive: subItem?.buttonIsActive || false,
          buttonIsDisabled: subItem?.buttonIsDisabled || false,
        }));

      // Adicionar dropdownId e dropdownEvent para que o item do nível 2 tenha dropdown no menu secundário
      secondaryMenuItem.dropdownId = `dropdown--${itemButtonId}`;
      secondaryMenuItem.dropdownEvent = 'click';
    } else if (includeNestedItems) {
      // Se não tiver buttonDropdownMenuListData, buscar sub-itens no allMenuItems
      const subItems = allMenuItems?.filter(
        (subItem: any) => subItem?.relativeParentId === itemButtonId,
      );

      if (subItems && subItems.length > 0) {
        // Mapear os sub-itens diretamente
        secondaryMenuItem.buttonDropdownMenuListData = subItems.map(
          (subItem: any) => {
            const subItemButtonId = subItem?.buttonId || subItem?.id;
            return {
              buttonId: subItemButtonId,
              buttonText: subItem?.buttonText,
              buttonOnClickFunc: subItem?.buttonOnClickFunc,
              buttonIsActive: subItem?.buttonIsActive || false,
              buttonIsDisabled: subItem?.buttonIsDisabled || false,
            };
          },
        );

        // Adicionar dropdownId e dropdownEvent
        secondaryMenuItem.dropdownId = `dropdown--${itemButtonId}`;
        secondaryMenuItem.dropdownEvent = 'click';
      }
    }

    return secondaryMenuItem;
  });
};

export const formatMenuButtons = ({
  items,
  dropdownItems,
  relativeParentId,
  allMenuItems,
  includeSecondaryMenuItems = false,
}: any) => {
  if (!items || !Array.isArray(items)) {
    return includeSecondaryMenuItems
      ? { items: [], secondaryMenuItems: [] }
      : [];
  }

  const secondaryMenuItems: any[] = [];

  const formattedItems = items.map((button: any) => {
    const result: any = {
      buttonId: button?.buttonId,
      buttonText: button?.buttonText,
      buttonOnClickFunc: button?.buttonOnClickFunc,
      buttonIsActive: button?.buttonIsActive || false,
      buttonIsDisabled: button?.buttonIsDisabled || false,
    };

    // Se houver dropdownItems e o botão corresponder ao relativeParentId
    if (
      relativeParentId &&
      button?.buttonId === relativeParentId &&
      dropdownItems &&
      dropdownItems?.length > 0
    ) {
      // Processar os itens do dropdown e seus sub-dropdowns recursivamente
      const allItems = allMenuItems || dropdownItems;
      const dropdownId = result.dropdownId || `dropdown--${relativeParentId}`;
      result.dropdownId = dropdownId;
      result.dropdownEvent = 'click';

      const processedDropdownItems = processDropdownItems(
        dropdownItems,
        allItems,
        dropdownId,
      );

      result.buttonDropdownMenuListData = processedDropdownItems;

      // Se solicitado, extrair os itens do dropdown para o menu secundário
      if (includeSecondaryMenuItems) {
        const extractedItems = extractDropdownItemsForSecondaryMenu(
          dropdownItems,
          allItems,
        );
        secondaryMenuItems.push(...extractedItems);
      }
    }

    return result;
  });

  // Se includeSecondaryMenuItems for true, retornar objeto com items e secondaryMenuItems
  if (includeSecondaryMenuItems) {
    return {
      items: formattedItems,
      secondaryMenuItems,
    };
  }

  return formattedItems;
};

export const getPrimaryMenuFormatted = ({ menu, relativeParentId }: any) => {
  // Verificar se o menu é válido
  if (!menu || !Array.isArray(menu) || menu.length === 0) {
    return [];
  }

  // Verificar se o menu é um array simples de itens ou um array com buttonGroupArray
  const isSimpleArray = menu?.length > 0 && !menu[0]?.buttonGroupArray;

  if (isSimpleArray) {
    // Processar array simples - construir hierarquia completa recursivamente
    const allMenuItems = menu;

    // Encontrar itens do nível raiz (sem parent ou com relativeParentId null)
    const rootItems = allMenuItems.filter(
      (item: any) => !item?.relativeParentId || item?.relativeParentId === null,
    );

    // Processar recursivamente começando pelos itens raiz
    return rootItems.map((rootItem: any) => {
      const itemButtonId = rootItem?.buttonId || rootItem?.id;

      // Buscar filhos diretos deste item
      const children = allMenuItems.filter(
        (item: any) => item?.relativeParentId === itemButtonId,
      );

      const result: any = {
        buttonId: itemButtonId,
        buttonText: rootItem?.buttonText,
        buttonOnClickFunc: rootItem?.buttonOnClickFunc,
        buttonIsActive: rootItem?.buttonIsActive || false,
        buttonIsDisabled: rootItem?.buttonIsDisabled || false,
      };

      // Se houver filhos, processar recursivamente
      if (children && children.length > 0) {
        const dropdownId = `dropdown--${itemButtonId}`;
        result.dropdownId = dropdownId;
        result.dropdownEvent = 'click';
        result.buttonDropdownMenuListData = processDropdownItems(
          children,
          allMenuItems,
          dropdownId,
        );
      } else {
        // Garantir que não há dropdown se não houver filhos
        result.buttonDropdownMenuListData = undefined;
      }

      return result;
    });
  }

  // Processamento original para estrutura com buttonGroupArray
  const allMenuItems = menu?.flatMap((menuItem: any) =>
    menuItem?.buttonGroupArray ? menuItem?.buttonGroupArray : [],
  );

  const primaryMenuDropdownItems = menu?.flatMap((menuItem: any) =>
    menuItem?.buttonGroupArray
      ? menuItem?.buttonGroupArray?.filter(
          (item: any) => item?.relativeParentId === relativeParentId,
        )
      : [],
  );

  const primaryMenu = menu?.flatMap((menuItem: any) =>
    menuItem?.buttonGroupArray
      ? menuItem?.buttonGroupArray?.filter(
          (item: any) => item?.relativeParentId !== relativeParentId,
        )
      : menuItem,
  );

  const formattedPrimaryMenu = formatMenuButtons({
    items: primaryMenu,
    dropdownItems: primaryMenuDropdownItems,
    relativeParentId: relativeParentId || 'notificationPharmacyMenu',
    allMenuItems, // Passar todos os itens para processamento recursivo
  });

  return formattedPrimaryMenu || [];
};

// Função para extrair itens do dropdown do menu principal para o menu secundário
// IMPORTANTE: Esta função extrai APENAS os itens do nível 2 (filhos diretos do menu principal)
// Os itens do nível 3 ficam dentro do buttonDropdownMenuListData dos itens do nível 2
export const getSecondaryMenuItemsFromPrimary = (
  primaryMenu: any[],
  allMenuItems: any[],
): any[] => {
  if (!primaryMenu || !Array.isArray(primaryMenu)) {
    return [];
  }

  const secondaryMenuItems: any[] = [];

  primaryMenu.forEach((item: any) => {
    // Se o item tem dropdown, extrair os itens do dropdown (nível 2)
    if (
      item.buttonDropdownMenuListData &&
      item.buttonDropdownMenuListData.length > 0
    ) {
      // Extrair apenas os itens do nível 2 (filhos diretos do menu principal)
      // Os itens do nível 3 já estão dentro do buttonDropdownMenuListData dos itens do nível 2
      const extractedItems = extractDropdownItemsForSecondaryMenu(
        item.buttonDropdownMenuListData.map((dropdownItem: any) => {
          // Buscar o item original no allMenuItems para manter todas as propriedades
          const originalItem = allMenuItems.find(
            (menuItem: any) =>
              (menuItem.buttonId || menuItem.id) === dropdownItem.buttonId,
          );
          // Se o item original tiver buttonDropdownMenuListData, manter essa estrutura
          // Isso garante que os itens do nível 3 fiquem dentro do dropdown do nível 2
          if (originalItem) {
            return {
              ...originalItem,
              // Manter o buttonDropdownMenuListData se existir (itens do nível 3)
              buttonDropdownMenuListData:
                dropdownItem.buttonDropdownMenuListData,
            };
          }
          return dropdownItem;
        }),
        allMenuItems,
      );
      secondaryMenuItems.push(...extractedItems);
    }
  });

  return secondaryMenuItems;
};
